import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Grid,
  GridItem,
  Heading,
  Text,
  VStack,
  HStack,
  Tag,
  useColorModeValue,
  Card,
  CardBody,
  CardHeader,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Center,
  Spinner,
  useToast,
} from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

import { Panel } from "@bemap/ui";
import { getGroupAnalysis } from "../../services/assessments-service";
import { ANALYST_ROUTES } from "@bemap/utils";

interface GroupAnalysisData {
  totalAssessments: number;
  dominantProfile: {
    short: string;
    extended: string;
    count: number;
    percentage: number;
  };
  profilesDistribution: Array<{
    name: string;
    letter: string;
    count: number;
    percentage: number;
    averageIntensity: number;
  }>;
  topTalents: Array<{
    name: string;
    count: number;
    percentage: number;
    averageIntensity: number;
    description: string;
  }>;
  topCompetences: Array<{
    name: string;
    count: number;
    percentage: number;
    averageIntensity: number;
    description: string;
    primary: string;
  }>;
  situationalIndexes: Array<{
    name: string;
    average: number;
    intensityName: string;
    description: string;
  }>;
  assessments: Array<{
    id: number;
    profileName: string;
    profileEmail: string;
    companyName: string;
    originalProfile: string;
    dominantProfile: string;
    createdAt: string;
  }>;
}

const GroupAnalysisPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [analysisData, setAnalysisData] = useState<GroupAnalysisData | null>(null);

  const assessmentIds = location.state?.assessmentIds || [];
  const cardBg = useColorModeValue("white", "gray.700");
  const textColor = useColorModeValue("gray.700", "white");

  useEffect(() => {
    if (assessmentIds.length === 0) {
      navigate(ANALYST_ROUTES.ASSESSMENTS);
      return;
    }
    loadGroupAnalysis();
  }, [assessmentIds]);

  const loadGroupAnalysis = async () => {
    setIsLoading(true);
    try {
      const response = await getGroupAnalysis(assessmentIds);
      setAnalysisData(response.message);
    } catch (error) {
      console.error('Error loading group analysis:', error);
      toast({
        title: t('general.error') || 'Error',
        description: t('analyst.group_analysis_error') || 'Failed to load group analysis data. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getProfileColors = () => [
    '#e7001d', // Red for Executor
    '#ffc000', // Teal for Comunicador
    '#00aa30', // Blue for Planejador
    '#009ed3', // Green for Analista
  ];

  const getTalentColors = () => [
    '#FF8A80', '#FFB74D', '#81C784', '#64B5F6', '#BA68C8',
    '#F06292', '#4DB6AC', '#AED581', '#90CAF9', '#CE93D8'
  ];

  const getCompetenceColors = () => [
    '#FFCDD2', '#F8BBD9', '#E1BEE7', '#D1C4E9', '#C5CAE9',
    '#BBDEFB', '#B3E5FC', '#B2EBF2', '#B2DFDB', '#C8E6C9'
  ];

  // Custom tooltip component
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box bg={cardBg} p={3} borderRadius="md" border="1px" borderColor="gray.200" boxShadow="lg">
          <Text fontWeight="bold" color={textColor}>{data.name}</Text>
          <Text color={textColor}>
            {data.percentage?.toFixed(1)}%
          </Text>
        </Box>
      );
    }
    return null;
  };

  const formatProfilesData = () => {
    return analysisData?.profilesDistribution.map((profile, index) => ({
      name: profile.name,
      value: profile.percentage,
      percentage: profile.percentage,
      fill: getProfileColors()[index]
    })) || [];
  };

  const formatTalentsData = () => {
    return analysisData?.topTalents.slice(0, 5).map((talent, index) => ({
      name: talent.name,
      value: talent.averageIntensity,
      percentage: talent.averageIntensity,
      fill: getTalentColors()[index]
    })) || [];
  };

  const formatCompetencesData = () => {
    return analysisData?.topCompetences.slice(0, 5).map((competence, index) => ({
      name: competence.name,
      value: competence.averageIntensity,
      percentage: competence.averageIntensity,
      fill: getCompetenceColors()[index]
    })) || [];
  };

  if (isLoading) {
    return (
      <Center h="400px">
        <Spinner size="xl" />
      </Center>
    );
  }

  if (!analysisData) {
    return (
      <VStack spacing={4} p={4}>
        <Text>{t('general.no_data_available') || 'No data available'}</Text>
        <Button leftIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
          {t('actions.back') || 'Back'}
        </Button>
      </VStack>
    );
  }

  return (
    <VStack spacing={6} p={2}>
      {/* Header with back button and summary */}
      <HStack justify="space-between" w="100%">
        <VStack align="start">
          <Heading size="lg">{t('analyst.group_analysis') || 'Group Analysis'}</Heading>
        </VStack>
        <Button leftIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} variant="outline">
          {t('actions.back') || 'Back'}
        </Button>
      </HStack>

      {/* Dominant Profile and Total Assessments Cards */}
      <Grid templateColumns={{ base: "1fr", md: "2fr 1fr" }} gap={6} w="100%" templateRows="1fr">
        {/* Dominant Profile Card */}
        <GridItem>
          <Card bg={cardBg} variant="simple" w="100%" h="100%">
            <CardHeader>
              <Heading size="md" color={textColor}>{t('analyst.dominant_profile') || 'Dominant Profile'}</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={4} align="start" h="100%">
                <HStack>
                  <Tag size="lg" colorScheme="blue" variant="solid">
                    {analysisData.dominantProfile.short}
                  </Tag>
                  <Text fontSize="lg" fontWeight="bold" color={textColor}>
                    {analysisData.dominantProfile.extended}
                  </Text>
                </HStack>
                <Text color={textColor}>
                  {t('analyst.appears_in') || 'Appears in'} {analysisData.dominantProfile.count} {t('analyst.assessments') || 'assessments'}
                  ({analysisData.dominantProfile.percentage.toFixed(1)}%)
                </Text>
              </VStack>
            </CardBody>
          </Card>
        </GridItem>

        {/* Total Assessments Card */}
        <GridItem>
          <Card bg={cardBg} variant="simple" w="100%" h="100%">
            <CardHeader>
              <Heading size="md" color={textColor}>{t('analyst.total_assessments') || 'Total Assessments'}</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={4} align="center" justify="center" h="100%">
                <Text fontSize="4xl" fontWeight="bold" color="blue.500">
                  {analysisData.totalAssessments}
                </Text>
                <Text color={textColor} textAlign="center">
                  {t('analyst.assessments_analyzed') || 'Assessments Analyzed'}
                </Text>
              </VStack>
            </CardBody>
          </Card>
        </GridItem>
      </Grid>

      {/* Charts Grid */}
      <Grid templateColumns={{ base: "1fr", lg: "repeat(3, 1fr)" }} gap={4} w="100%">
        {/* Profiles Distribution */}
        <GridItem>
          <Panel>
            <VStack spacing={3} p={3}>
              <Heading size="md" color={textColor}>{t('analyst.profiles_distribution') || 'Profiles Distribution'}</Heading>
              <Box h="350px" w="100%">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={formatProfilesData()}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {formatProfilesData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <VStack spacing={2} align="start" w="100%">
                {analysisData.profilesDistribution.map((profile, index) => (
                  <VStack key={profile.letter} spacing={1} align="start" w="100%">
                    <HStack justify="space-between" w="100%">
                      <HStack>
                        <Box w={4} h={4} bg={getProfileColors()[index]} borderRadius="sm" />
                        <Text fontSize="sm" color={textColor}>{profile.name} ({profile.letter})</Text>
                      </HStack>
                      <Text fontSize="sm" color={textColor}>
                        {profile.percentage.toFixed(2)}%
                      </Text>
                    </HStack>
                  </VStack>
                ))}
              </VStack>
            </VStack>
          </Panel>
        </GridItem>

        {/* Top Talents */}
        <GridItem>
          <Panel>
            <VStack spacing={3} p={3}>
              <Heading size="md" color={textColor}>{t('analyst.top_talents') || 'Top Talents'}</Heading>
              <Box h="350px" w="100%">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={formatTalentsData()}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {formatTalentsData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <VStack spacing={2} align="start" w="100%">
                {analysisData.topTalents.slice(0, 5).map((talent, index) => (
                  <VStack key={talent.name} spacing={1} align="start" w="100%">
                    <HStack justify="space-between" w="100%">
                      <HStack>
                        <Box w={4} h={4} bg={getTalentColors()[index]} borderRadius="sm" />
                        <Text fontSize="sm" color={textColor}>{talent.name}</Text>
                      </HStack>
                      <Text fontSize="sm" color={textColor}>
                        {talent.averageIntensity.toFixed(2)}%
                      </Text>
                    </HStack>
                  </VStack>
                ))}
              </VStack>
            </VStack>
          </Panel>
        </GridItem>

        {/* Top Competences */}
        <GridItem>
          <Panel>
            <VStack spacing={3} p={3}>
              <Heading size="md" color={textColor}>{t('analyst.top_competences') || 'Top Competences'}</Heading>
              <Box h="350px" w="100%">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={formatCompetencesData()}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {formatCompetencesData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <VStack spacing={2} align="start" w="100%">
                {analysisData.topCompetences.slice(0, 5).map((competence, index) => (
                  <VStack key={competence.name} spacing={1} align="start" w="100%">
                    <HStack justify="space-between" w="100%">
                      <HStack>
                        <Box w={4} h={4} bg={getCompetenceColors()[index]} borderRadius="sm" />
                        <Text fontSize="sm" color={textColor}>{competence.name}</Text>
                      </HStack>
                      <Text fontSize="sm" color={textColor}>
                        {competence.averageIntensity.toFixed(2)}%
                      </Text>
                    </HStack>
                  </VStack>
                ))}
              </VStack>
            </VStack>
          </Panel>
        </GridItem>
      </Grid>

      {/* Situational Indexes */}
      <Panel>
        <VStack spacing={4} p={4}>
          <Heading size="md" color={textColor}>{t('analyst.situational_indexes') || 'Situational Indexes'}</Heading>
          <Grid templateColumns="repeat(auto-fit, minmax(250px, 1fr))" gap={4} w="100%">
            {analysisData.situationalIndexes.map((index) => (
              <Card key={index.name} bg={cardBg} variant="outline">
                <CardBody>
                  <VStack spacing={2} align="start">
                    <Text fontWeight="bold" color={textColor}>{index.name}</Text>
                    <HStack>
                      <Text fontSize="2xl" fontWeight="bold" color="blue.500">
                        {index.average.toFixed(1)}
                      </Text>
                      <Tag size="sm" colorScheme="blue" variant="outline">
                        {index.intensityName}
                      </Tag>
                    </HStack>
                    <Text fontSize="sm" color={textColor} noOfLines={3}>
                      {index.description}
                    </Text>
                  </VStack>
                </CardBody>
              </Card>
            ))}
          </Grid>
        </VStack>
      </Panel>

      {/* Selected Assessments Table */}
      <Panel heading={t('analyst.selected_assessments') || 'Selected Assessments'}>
        <TableContainer w="100%">
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th color={textColor}>{t('analyst.assesments_table_name') || 'Name'}</Th>
                <Th color={textColor}>{t('analyst.assesments_table_email') || 'Email'}</Th>
                <Th color={textColor}>{t('analyst.assesments_table_company') || 'Company'}</Th>
                <Th color={textColor}>{t('analyst.assesments_table_profile') || 'Profile'}</Th>
                <Th color={textColor}>{t('analyst.assesments_table_created') || 'Created'}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {analysisData.assessments.map((assessment) => (
                <Tr key={assessment.id}>
                  <Td color={textColor}>{assessment.profileName}</Td>
                  <Td color={textColor}>{assessment.profileEmail}</Td>
                  <Td color={textColor}>{assessment.companyName || t('general.not_available') || 'N/A'}</Td>
                  <Td>
                    <Tag size="sm" variant="outline" colorScheme="blue">
                      {assessment.originalProfile}
                    </Tag>
                  </Td>
                  <Td color={textColor}>{assessment.createdAt}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </Panel>
    </VStack>
  );
};

export default GroupAnalysisPage; 